import { useEffect, useRef } from 'react';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { createSseErrorReporter } from '@/utils/reportSseError';
import { useQueryClient } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';
import { GetStorageComponent } from '@/utils/session';
import { getEnv } from '@/utils/texts';
import type { IAtsConnection, IAtsConnectionResponse } from '../api/query';
import { ATS_QUERY_KEYS } from '../api/queryKeys';

type RawAtsCache = AxiosResponse<IAtsConnectionResponse<IAtsConnection[]>>;

interface NewJobsDetectedPayload {
  companyId: number;
  hasNewJobs?: boolean;
  lastSyncedAt?: string | null;
  lastCronSyncAt?: string | null;
}

interface JobsClearedPayload {
  companyId: number;
}

// Fired for a candidate that arrived (or had its stage changed) via a live ATS webhook — the
// manual "Sync now" flow has its own per-syncConfigId SSE stream for this (useAtsSyncStream), but
// a webhook delivery never has a syncConfigId, so this company-wide channel is the only live
// signal the frontend gets for it. See resume-parsing's ats-candidate-sync.service.ts
// (ATS_NOTIFICATION_EVENT) for the publisher side.
interface CandidateAddedPayload {
  jobId: string;
  atsJobId?: string;
  jobName?: string;
  count?: number;
}

interface CandidateStageUpdatedPayload {
  jobId: string;
  atsJobId?: string;
  stage?: string | null;
}

export interface JobLockedPayload {
  // The stream sends this as a string on live events but as a number on replay-on-connect events.
  jobId: string | number;
  flow?: string;
  startedAt?: string;
}

export interface JobUnlockedPayload {
  jobId: string | number;
}

interface UseAtsNotificationsStreamOptions {
  onJobLocked?: (payload: JobLockedPayload) => void;
  onJobUnlocked?: (payload: JobUnlockedPayload) => void;
}

const updateAtsConnectionCache = (
  previous: RawAtsCache | undefined,
  companyId: number,
  updater: (connection: IAtsConnection) => IAtsConnection,
): RawAtsCache | undefined => {
  if (!previous) return previous;

  return {
    ...previous,
    data: {
      ...previous.data,
      data: previous.data.data.map(connection =>
        connection.companyId === companyId ? updater(connection) : connection,
      ),
    },
  };
};

const useAtsNotificationsStream = (
  companyId?: number | null,
  enabled = true,
  options?: UseAtsNotificationsStreamOptions,
) => {
  const queryClient = useQueryClient();
  const abortRef = useRef<AbortController | null>(null);
  // Keep the latest callbacks in a ref so changing them doesn't tear down and re-open the SSE.
  const callbacksRef = useRef(options);
  useEffect(() => {
    callbacksRef.current = options;
  });

  useEffect(() => {
    if (!enabled || !companyId) return;

    const token = GetStorageComponent('accessToken') ?? '';
    const idToken = GetStorageComponent('idToken') ?? '';
    const base = (getEnv('VITE_JOB_CAMPAIGN_BASE_URL') ?? '').replace(/\/$/, '');
    const url = `${base}/jobs/ats/notifications/${companyId}/stream`;
    const controller = new AbortController();
    const reportSseError = createSseErrorReporter({ feature: 'ats-notifications', operation: 'sse-error' });
    abortRef.current = controller;

    void fetchEventSource(url, {
      method: 'GET',
      signal: controller.signal,
      openWhenHidden: true,
      headers: {
        Accept: 'text/event-stream',
        Authorization: token ? `Bearer ${token}` : '',
        ...(idToken ? { 'x-id-token': idToken } : {}),
        'Cache-Control': 'no-cache',
        Connection: 'keep-alive',
      },
      onopen: async response => {
        if (!response.ok) throw new Error(`SSE connection failed with status ${response.status}`);
      },
      onmessage: event => {
        if (event.event === 'new_jobs_detected') {
          const data: NewJobsDetectedPayload = JSON.parse(event.data);
          queryClient.setQueryData<RawAtsCache | undefined>(ATS_QUERY_KEYS.connections, previous =>
            updateAtsConnectionCache(previous, data.companyId, connection => ({
              ...connection,
              hasNewJobs: true,
              lastSyncedAt: data.lastSyncedAt ?? connection.lastSyncedAt,
              lastCronSyncAt: data.lastCronSyncAt ?? connection.lastCronSyncAt,
            })),
          );
          return;
        }

        if (event.event === 'jobs_cleared') {
          const data: JobsClearedPayload = JSON.parse(event.data);
          queryClient.setQueryData<RawAtsCache | undefined>(ATS_QUERY_KEYS.connections, previous =>
            updateAtsConnectionCache(previous, data.companyId, connection => ({
              ...connection,
              hasNewJobs: false,
            })),
          );
          return;
        }

        if (event.event === 'job_locked') {
          const data: JobLockedPayload = JSON.parse(event.data);
          callbacksRef.current?.onJobLocked?.(data);
          return;
        }

        if (event.event === 'job_unlocked') {
          const data: JobUnlockedPayload = JSON.parse(event.data);
          callbacksRef.current?.onJobUnlocked?.(data);
          return;
        }

        if (event.event === 'candidate_added') {
          const data: CandidateAddedPayload = JSON.parse(event.data);
          // Silent refetch only — no toast. Matches the query key used by
          // useGetCandidatesByJob/candidateQueries.ts mutations (GET_CANDIDATES_BY_JOB, jobId),
          // partial-matched so it also invalidates any filter variant.
          void queryClient.invalidateQueries({ queryKey: ['GET_CANDIDATES_BY_JOB', data.jobId] });
          return;
        }

        if (event.event === 'candidate_stage_updated') {
          const data: CandidateStageUpdatedPayload = JSON.parse(event.data);
          void queryClient.invalidateQueries({ queryKey: ['GET_CANDIDATES_BY_JOB', data.jobId] });
        }
      },
      onerror: error => {
        if (controller.signal.aborted) return;
        reportSseError(error);
        // Returning (not throwing) tells fetchEventSource to keep retrying with its default
        // backoff instead of giving up permanently on the first drop.
      },
      onclose: () => {
        if (controller.signal.aborted) return;
        abortRef.current = null;
      },
    });

    return () => {
      controller.abort();
      abortRef.current = null;
    };
  }, [companyId, enabled, queryClient]);
};

export default useAtsNotificationsStream;
